import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Eye, X, FileText } from 'lucide-react';
import { sapi } from '../../lib/api';
import { Spinner, useToast, Toast, ConfirmModal } from '../../components/UI';

interface CoverLetter { id: string; name: string; body: string; createdAt: string; }

const PH = ['{{company_name}}','{{address}}','{{manager_name}}','{{job_title}}','{{admirables}}','{{your_name}}','{{date}}'];

const DEFAULT_BODY = `Dear {{manager_name}},

I am writing to express my keen interest in the {{job_title}} position at {{company_name}}. Having researched your company, I particularly admire {{admirables}}, and I believe my skills and passion make me an excellent fit for your team.

[Your introduction — describe yourself and your background]

[Body paragraph — describe relevant skills and experience]

[Closing paragraph — express enthusiasm and call to action]

I would welcome the opportunity to discuss how I can contribute to {{company_name}}. Thank you for your time and consideration.`;

export default function CoverLettersPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [items, setItems] = useState<CoverLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'add' | CoverLetter | null>(null);
  const [preview, setPreview] = useState<CoverLetter | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CoverLetter | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', body: DEFAULT_BODY });
  const [bodyRef, setBodyRef] = useState<HTMLTextAreaElement | null>(null);

  useEffect(() => { sapi.getCoverLetters().then(setItems).catch(() => setItems([])).finally(() => setLoading(false)); }, []);

  const openAdd = () => { setForm({ name: '', body: DEFAULT_BODY }); setModal('add'); };
  const openEdit = (cl: CoverLetter) => { setForm({ name: cl.name, body: cl.body }); setModal(cl); };

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) { show('Name and body required', 'error'); return; }
    setSaving(true);
    try {
      if (modal === 'add') {
        const created = await sapi.createCoverLetter(form.name, form.body);
        setItems(p => [created, ...p]); show(`"${form.name}" saved`, 'success');
      } else if (modal && typeof modal === 'object') {
        const updated = await sapi.updateCoverLetter(modal.id, form.name, form.body);
        setItems(p => p.map(i => i.id === updated.id ? updated : i)); show('Updated', 'success');
      }
      setModal(null);
    } catch (e: any) { show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    await sapi.deleteCoverLetter(deleteTarget.id).catch(() => null);
    setItems(p => p.filter(i => i.id !== deleteTarget.id));
    show('Deleted', 'info'); setDeleteTarget(null);
  };

  const insertPH = (ph: string) => {
    if (!bodyRef) return;
    const s = bodyRef.selectionStart, e = bodyRef.selectionEnd;
    const val = form.body.slice(0, s) + ph + form.body.slice(e);
    setForm(f => ({ ...f, body: val }));
    setTimeout(() => { bodyRef.focus(); bodyRef.setSelectionRange(s + ph.length, s + ph.length); }, 0);
  };

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <div className="page-header">
        <div><h1 className="page-title">Cover Letters</h1><p className="page-sub">{items.length} template{items.length !== 1 ? 's' : ''}</p></div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> New Template</button>
      </div>

      {loading ? <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><Spinner size={28} /></div>
      : items.length === 0 ? (
        <div className="card"><div className="empty-state"><FileText size={32} /><h3>No cover letter templates</h3><p>Create a reusable cover letter template with placeholders.</p><button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> New Template</button></div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {items.map(cl => (
            <div key={cl.id} className="card" style={{ borderLeft: '3px solid var(--accent)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{cl.name}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPreview(cl)}><Eye size={12} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(cl)}><Edit2 size={12} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(cl)}><Trash2 size={12} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>{cl.body}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date(cl.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal !== null && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal modal-wide">
            <div className="modal-header"><h3>{modal === 'add' ? 'New Cover Letter Template' : `Edit — ${(modal as CoverLetter).name}`}</h3><button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}><X size={14} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label>Template Name *</label><input placeholder="e.g. Software Dev Cover Letter" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus /></div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Body *</label>
                <textarea ref={setBodyRef} value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} style={{ minHeight: 280, fontFamily: 'DM Sans, sans-serif', fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>Insert:</span>
                {PH.map(ph => <button key={ph} className="btn btn-ghost btn-sm" style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }} onClick={() => insertPH(ph)}>{ph}</button>)}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : null}{modal === 'add' ? 'Save Template' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPreview(null)}>
          <div className="modal modal-wide">
            <div className="modal-header"><h3>{preview.name}</h3><button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}><X size={14} /></button></div>
            <div className="modal-body"><div className="preview-box" style={{ whiteSpace: 'pre-wrap', minHeight: 200 }}>{preview.body}</div></div>
          </div>
        </div>
      )}

      {deleteTarget && <ConfirmModal title="Delete Template" message={<>Delete <strong>{deleteTarget.name}</strong>?</>} onConfirm={del} onCancel={() => setDeleteTarget(null)} danger />}
    </div>
  );
}
